import { useEffect, useMemo, useState } from 'react'
import { liveCollection } from '../data/collectes'
import { distanceKm, routeLengthKm } from '../utils/geo'

type Point = [number, number]

const AVERAGE_SPEED_KMH = 12
const TICK_MS = 700
const PROGRESS_PER_TICK = 0.004
const FRAME_MS = 50
/** Durée de l'avance accélérée jusqu'à la maison. */
const BOOST_MS = 3000

/**
 * Progression commune, partagée par la carte du module et l'aperçu d'ATNA.
 * Le camion n'avance que lorsqu'au moins une carte est affichée à l'écran.
 */
let progress = liveCollection.startProgress
let viewers = 0
let boost: { from: number; startedAt: number } | null = null
let timer: number | null = null
let lastTick = Date.now()
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((listener) => listener())
}

function step() {
  const now = Date.now()
  const elapsed = now - lastTick
  lastTick = now
  if (boost) {
    const ratio = Math.min(1, (now - boost.startedAt) / BOOST_MS)
    // ease-out : le camion ralentit en arrivant devant la maison
    const eased = 1 - Math.pow(1 - ratio, 3)
    progress = boost.from + (1 - boost.from) * eased
    if (ratio >= 1) boost = null
  } else {
    progress = Math.min(1, progress + (elapsed / TICK_MS) * PROGRESS_PER_TICK)
  }
  emit()
  if (progress >= 1) stopTimer()
}

function startTimer() {
  if (timer !== null || progress >= 1) return
  lastTick = Date.now()
  timer = window.setInterval(step, boost ? FRAME_MS : TICK_MS)
}

function stopTimer() {
  if (timer === null) return
  window.clearInterval(timer)
  timer = null
}

function restartTimer() {
  stopTimer()
  if (viewers > 0 || boost) startTimer()
}

/** Replace le camion à son point de départ. */
export function resetLiveTruck() {
  progress = liveCollection.startProgress
  boost = null
  restartTimer()
  emit()
}

/** Démo : le camion file jusqu'à la maison en 3 secondes. */
export function boostLiveTruck() {
  if (progress >= 0.999 || boost) return
  boost = { from: progress, startedAt: Date.now() }
  restartTimer()
}

/** À appeler par chaque carte affichée : tant qu'aucune n'est visible, le camion reste immobile. */
export function useTruckViewer() {
  useEffect(() => {
    viewers += 1
    if (viewers === 1) startTimer()
    return () => {
      viewers -= 1
      if (viewers === 0 && !boost) stopTimer()
    }
  }, [])
}

function useProgress(): number {
  const [value, setValue] = useState(progress)
  useEffect(() => {
    const listener = () => setValue(progress)
    listeners.add(listener)
    listener()
    return () => {
      listeners.delete(listener)
    }
  }, [])
  return value
}

/** Trajet routier réel (OSRM, sans clé), récupéré une seule fois et partagé. */
let cachedRoute: Point[] | null = null
let routeRequest: Promise<Point[] | null> | null = null

function loadRoute(): Promise<Point[] | null> {
  if (cachedRoute) return Promise.resolve(cachedRoute)
  if (routeRequest) return routeRequest
  const start = liveCollection.route[0]
  const home = liveCollection.home
  const url = `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${home[1]},${home[0]}?overview=full&geometries=geojson`

  routeRequest = fetch(url)
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error('routing'))))
    .then((data: { routes?: { geometry: { coordinates: [number, number][] } }[] }) => {
      const coordinates = data.routes?.[0]?.geometry?.coordinates
      if (!coordinates || coordinates.length < 2) return null
      cachedRoute = coordinates.map(([lng, lat]) => [lat, lng] as Point)
      return cachedRoute
    })
    .catch(() => {
      routeRequest = null // nouvel essai possible ; en attendant, trajet approximatif
      return null
    })
  return routeRequest
}

function cumulativeLengths(points: Point[]): number[] {
  const totals = [0]
  for (let i = 1; i < points.length; i += 1) totals.push(totals[i - 1] + distanceKm(points[i - 1], points[i]))
  return totals
}

function splitRoute(points: Point[], totals: number[], progress: number) {
  const target = totals[totals.length - 1] * progress
  const found = totals.findIndex((value) => value >= target)
  const index = found <= 0 ? 1 : found
  const previous = totals[index - 1]
  const ratio = (target - previous) / Math.max(totals[index] - previous, 1e-9)
  const [lat1, lng1] = points[index - 1]
  const [lat2, lng2] = points[index]
  const truck: Point = [lat1 + (lat2 - lat1) * ratio, lng1 + (lng2 - lng1) * ratio]
  return {
    done: [...points.slice(0, index), truck] as Point[],
    remaining: [truck, ...points.slice(index)] as Point[],
    truck,
  }
}

/** Camion de la collecte du jour : trajet sur les rues, position en direct et arrivée estimée. */
export function useLiveTruck() {
  const [route, setRoute] = useState<Point[]>(cachedRoute ?? liveCollection.route)
  const progress = useProgress()

  useEffect(() => {
    let cancelled = false
    loadRoute().then((loaded) => {
      if (!cancelled && loaded) setRoute(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const arrived = progress >= 0.999

  const totals = useMemo(() => cumulativeLengths(route), [route])
  const { done, remaining, truck } = useMemo(() => splitRoute(route, totals, progress), [route, totals, progress])

  const distance = routeLengthKm(remaining)
  const minutes = Math.max(1, Math.round((distance / AVERAGE_SPEED_KMH) * 60))

  return {
    route,
    done,
    remaining,
    truckPosition: truck,
    distance,
    minutes,
    arrived,
    boosting: boost !== null,
    arrivingSoon: minutes <= 10,
  }
}
